import type { CompetitorComparisonContent } from '@/pages/public/competitor-comparison';

export const competitorComparisonContent: Record<string, CompetitorComparisonContent> = {
  '/zoho-crm-alternative': {
    path: '/zoho-crm-alternative',
    eyebrow: 'Comparing Zoho CRM and LeaderDaddy',
    heroTitle: 'Zoho CRM or LeaderDaddy: compare your sales workflow',
    heroBody:
      'Evaluate Zoho CRM and LeaderDaddy against your lead capture, follow-up, integration and budget requirements. This comparison is published by LeaderDaddy; there is no single best CRM for every team.',
    heroCta: 'Try LeaderDaddy for 7 days',
    competitorName: 'Zoho CRM',
    reviewedAt: '2026-10-07',
    sourceUrl: 'https://www.zoho.com/crm/',
    competitorSummary:
      'Teams evaluating configurable CRM processes and a broader business-app ecosystem.',
    comparisonTitle: 'How to evaluate LeaderDaddy and Zoho CRM',
    comparisonDescription:
      'Reviewed 7 October 2026 using the vendor pages linked below and the LeaderDaddy implementation. Compare workflow fit, account requirements and total cost. This is a desk review, not a hands-on performance benchmark.',
    comparisonRows: [
      {
        sourceUrl: 'https://www.zoho.com/crm/',
        feature: 'Who to evaluate it for',
        leaderDaddy: 'Teams focused on assigned leads, follow-ups and connected WhatsApp workflows',
        competitor:
          'Teams evaluating configurable CRM processes and a broader business-app ecosystem.',
      },
      {
        sourceUrl: 'https://www.zoho.com/crm/',
        feature: 'Lead workflow',
        leaderDaddy: 'List and Kanban views, lead ownership and follow-up dates',
        competitor:
          'Sales CRM workflows; test your stages and assignment requirements in the relevant plan',
      },
      {
        sourceUrl: 'https://www.zoho.com/crm/',
        feature: 'WhatsApp',
        leaderDaddy:
          'Configured account connection, inbox and template broadcasts; Meta requirements apply',
        competitor:
          'Zoho documents WhatsApp integration; check current edition and messaging requirements',
      },
      {
        sourceUrl: 'https://www.zoho.com/crm/',
        feature: 'Limits to check',
        leaderDaddy:
          'Verify specialist integrations, customization and account eligibility in a demo; AI services can require wallet credits',
        competitor:
          'Verify plan features, seats, integrations and onboarding requirements against your workflow',
      },
    ],
    pricingTitle: 'Compare the full cost',
    pricingDescription:
      'Prices and entitlements can change. Use the linked pricing pages and written quotes for the same team and usage assumptions. LeaderDaddy subscription, Meta messaging and configured wallet-service charges are separate.',
    pricingRows: [
      {
        label: 'Subscription',
        leaderDaddy: 'Annual CRM subscription; current INR price on our pricing page',
        competitor: 'Review the current vendor plans and billing term',
      },
      {
        label: 'Messaging and optional services',
        leaderDaddy: 'Applicable Meta messaging and wallet usage charged separately',
        competitor: 'Confirm messaging, optional services and onboarding costs',
      },
      {
        label: 'Evaluation',
        leaderDaddy: '7-day CRM trial; verify the exact workflow you need',
        competitor: 'Check current free tools, trial or demo availability',
      },
    ],
    migrationTitle: 'Validate a migration before committing',
    migrationDescription:
      'Use a sample of non-sensitive lead data and compare both workflows before moving the team.',
    migrationSteps: [
      'List required fields, stages, owners and integrations.',
      'Test sample records and confirm data mapping with each vendor.',
      'Check role visibility and follow-up dates with your team.',
      'Confirm export, history and integration requirements before a full migration.',
    ],
    benefitsTitle: 'What to test in LeaderDaddy',
    benefits: [
      {
        title: 'Lead ownership',
        description: 'Assign a sample lead and confirm the correct person can access it.',
      },
      {
        title: 'Follow-up tracking',
        description: 'Set a due date, update the lead and inspect overdue follow-up views.',
      },
      {
        title: 'Messaging fit',
        description:
          'If required, connect an eligible WhatsApp account and test with a consenting recipient.',
      },
    ],
    industriesTitle: 'Choose by operating need',
    industries: ['Shared lead tracking', 'Sales follow-up coordination', 'Manager visibility'],
    faqs: [
      {
        question: 'Is LeaderDaddy always better than Zoho CRM?',
        answer:
          'No. Teams evaluating configurable CRM processes and a broader business-app ecosystem. LeaderDaddy is worth evaluating for focused lead management and connected WhatsApp workflows. Compare actual requirements, total cost and the trial experience.',
      },
      {
        question: 'Who publishes this comparison?',
        answer:
          'LeaderDaddy, a CRM by Toolcross LLP, publishes this comparison. It was reviewed on 7 October 2026 using vendor sources and repository evidence. Vendor claims are not independently tested results.',
      },
      {
        question: 'What should I verify before switching?',
        answer:
          'Test a sample import, role permissions, follow-up dates and essential integrations. Confirm the current price and any messaging or usage charges. Do not assume all data or integrations migrate automatically.',
      },
    ],
  },
  '/hubspot-alternative': {
    path: '/hubspot-alternative',
    eyebrow: 'Comparing HubSpot and LeaderDaddy',
    heroTitle: 'HubSpot or LeaderDaddy: compare your sales workflow',
    heroBody:
      'Evaluate HubSpot and LeaderDaddy against your lead capture, follow-up, integration and budget requirements. This comparison is published by LeaderDaddy; there is no single best CRM for every team.',
    heroCta: 'Try LeaderDaddy for 7 days',
    competitorName: 'HubSpot',
    reviewedAt: '2026-10-07',
    sourceUrl: 'https://www.hubspot.com/products/crm',
    competitorSummary:
      'Teams evaluating connected marketing, sales and service tools with a shared CRM.',
    comparisonTitle: 'How to evaluate LeaderDaddy and HubSpot',
    comparisonDescription:
      'Reviewed 7 October 2026 using the vendor pages linked below and the LeaderDaddy implementation. Compare workflow fit, account requirements and total cost. This is a desk review, not a hands-on performance benchmark.',
    comparisonRows: [
      {
        sourceUrl: 'https://www.hubspot.com/products/crm',
        feature: 'Who to evaluate it for',
        leaderDaddy: 'Teams focused on assigned leads, follow-ups and connected WhatsApp workflows',
        competitor:
          'Teams evaluating connected marketing, sales and service tools with a shared CRM.',
      },
      {
        sourceUrl: 'https://www.hubspot.com/products/crm',
        feature: 'Lead workflow',
        leaderDaddy: 'List and Kanban views, lead ownership and follow-up dates',
        competitor:
          'Sales CRM workflows; test your stages and assignment requirements in the relevant plan',
      },
      {
        sourceUrl: 'https://www.hubspot.com/products/crm',
        feature: 'WhatsApp',
        leaderDaddy:
          'Configured account connection, inbox and template broadcasts; Meta requirements apply',
        competitor:
          'Confirm the required channel integration, product tier and messaging costs with the vendor',
      },
      {
        sourceUrl: 'https://www.hubspot.com/products/crm',
        feature: 'Limits to check',
        leaderDaddy:
          'Verify specialist integrations, customization and account eligibility in a demo; AI services can require wallet credits',
        competitor:
          'Verify plan features, seats, integrations and onboarding requirements against your workflow',
      },
    ],
    pricingTitle: 'Compare the full cost',
    pricingDescription:
      'Prices and entitlements can change. Use the linked pricing pages and written quotes for the same team and usage assumptions. LeaderDaddy subscription, Meta messaging and configured wallet-service charges are separate.',
    pricingRows: [
      {
        label: 'Subscription',
        leaderDaddy: 'Annual CRM subscription; current INR price on our pricing page',
        competitor: 'Review the current vendor plans and billing term',
      },
      {
        label: 'Messaging and optional services',
        leaderDaddy: 'Applicable Meta messaging and wallet usage charged separately',
        competitor: 'Confirm messaging, optional services and onboarding costs',
      },
      {
        label: 'Evaluation',
        leaderDaddy: '7-day CRM trial; verify the exact workflow you need',
        competitor: 'Check current free tools, trial or demo availability',
      },
    ],
    migrationTitle: 'Validate a migration before committing',
    migrationDescription:
      'Use a sample of non-sensitive lead data and compare both workflows before moving the team.',
    migrationSteps: [
      'List required fields, stages, owners and integrations.',
      'Test sample records and confirm data mapping with each vendor.',
      'Check role visibility and follow-up dates with your team.',
      'Confirm export, history and integration requirements before a full migration.',
    ],
    benefitsTitle: 'What to test in LeaderDaddy',
    benefits: [
      {
        title: 'Lead ownership',
        description: 'Assign a sample lead and confirm the correct person can access it.',
      },
      {
        title: 'Follow-up tracking',
        description: 'Set a due date, update the lead and inspect overdue follow-up views.',
      },
      {
        title: 'Messaging fit',
        description:
          'If required, connect an eligible WhatsApp account and test with a consenting recipient.',
      },
    ],
    industriesTitle: 'Choose by operating need',
    industries: ['Shared lead tracking', 'Sales follow-up coordination', 'Manager visibility'],
    faqs: [
      {
        question: 'Is LeaderDaddy always better than HubSpot?',
        answer:
          'No. Teams evaluating connected marketing, sales and service tools with a shared CRM. LeaderDaddy is worth evaluating for focused lead management and connected WhatsApp workflows. Compare actual requirements, total cost and the trial experience.',
      },
      {
        question: 'Who publishes this comparison?',
        answer:
          'LeaderDaddy, a CRM by Toolcross LLP, publishes this comparison. It was reviewed on 7 October 2026 using vendor sources and repository evidence. Vendor claims are not independently tested results.',
      },
      {
        question: 'What should I verify before switching?',
        answer:
          'Test a sample import, role permissions, follow-up dates and essential integrations. Confirm the current price and any messaging or usage charges. Do not assume all data or integrations migrate automatically.',
      },
    ],
  },
  '/leadsquared-alternative': {
    path: '/leadsquared-alternative',
    eyebrow: 'Comparing LeadSquared and LeaderDaddy',
    heroTitle: 'LeadSquared or LeaderDaddy: compare your sales workflow',
    heroBody:
      'Evaluate LeadSquared and LeaderDaddy against your lead capture, follow-up, integration and budget requirements. This comparison is published by LeaderDaddy; there is no single best CRM for every team.',
    heroCta: 'Try LeaderDaddy for 7 days',
    competitorName: 'LeadSquared',
    reviewedAt: '2026-10-07',
    sourceUrl: 'https://www.leadsquared.com/',
    competitorSummary:
      'Teams evaluating high-volume lead handling and multi-stage sales execution.',
    comparisonTitle: 'How to evaluate LeaderDaddy and LeadSquared',
    comparisonDescription:
      'Reviewed 7 October 2026 using the vendor pages linked below and the LeaderDaddy implementation. Compare workflow fit, account requirements and total cost. This is a desk review, not a hands-on performance benchmark.',
    comparisonRows: [
      {
        sourceUrl: 'https://www.leadsquared.com/',
        feature: 'Who to evaluate it for',
        leaderDaddy: 'Teams focused on assigned leads, follow-ups and connected WhatsApp workflows',
        competitor: 'Teams evaluating high-volume lead handling and multi-stage sales execution.',
      },
      {
        sourceUrl: 'https://www.leadsquared.com/',
        feature: 'Lead workflow',
        leaderDaddy: 'List and Kanban views, lead ownership and follow-up dates',
        competitor:
          'Sales CRM workflows; test your stages and assignment requirements in the relevant plan',
      },
      {
        sourceUrl: 'https://www.leadsquared.com/',
        feature: 'WhatsApp',
        leaderDaddy:
          'Configured account connection, inbox and template broadcasts; Meta requirements apply',
        competitor:
          'Confirm the required channel integration, product tier and messaging costs with the vendor',
      },
      {
        sourceUrl: 'https://www.leadsquared.com/',
        feature: 'Limits to check',
        leaderDaddy:
          'Verify specialist integrations, customization and account eligibility in a demo; AI services can require wallet credits',
        competitor:
          'Verify plan features, seats, integrations and onboarding requirements against your workflow',
      },
    ],
    pricingTitle: 'Compare the full cost',
    pricingDescription:
      'Prices and entitlements can change. Use the linked pricing pages and written quotes for the same team and usage assumptions. LeaderDaddy subscription, Meta messaging and configured wallet-service charges are separate.',
    pricingRows: [
      {
        label: 'Subscription',
        leaderDaddy: 'Annual CRM subscription; current INR price on our pricing page',
        competitor: 'Review the current vendor plans and billing term',
      },
      {
        label: 'Messaging and optional services',
        leaderDaddy: 'Applicable Meta messaging and wallet usage charged separately',
        competitor: 'Confirm messaging, optional services and onboarding costs',
      },
      {
        label: 'Evaluation',
        leaderDaddy: '7-day CRM trial; verify the exact workflow you need',
        competitor: 'Check current free tools, trial or demo availability',
      },
    ],
    migrationTitle: 'Validate a migration before committing',
    migrationDescription:
      'Use a sample of non-sensitive lead data and compare both workflows before moving the team.',
    migrationSteps: [
      'List required fields, stages, owners and integrations.',
      'Test sample records and confirm data mapping with each vendor.',
      'Check role visibility and follow-up dates with your team.',
      'Confirm export, history and integration requirements before a full migration.',
    ],
    benefitsTitle: 'What to test in LeaderDaddy',
    benefits: [
      {
        title: 'Lead ownership',
        description: 'Assign a sample lead and confirm the correct person can access it.',
      },
      {
        title: 'Follow-up tracking',
        description: 'Set a due date, update the lead and inspect overdue follow-up views.',
      },
      {
        title: 'Messaging fit',
        description:
          'If required, connect an eligible WhatsApp account and test with a consenting recipient.',
      },
    ],
    industriesTitle: 'Choose by operating need',
    industries: ['Shared lead tracking', 'Sales follow-up coordination', 'Manager visibility'],
    faqs: [
      {
        question: 'Is LeaderDaddy always better than LeadSquared?',
        answer:
          'No. Teams evaluating high-volume lead handling and multi-stage sales execution. LeaderDaddy is worth evaluating for focused lead management and connected WhatsApp workflows. Compare actual requirements, total cost and the trial experience.',
      },
      {
        question: 'Who publishes this comparison?',
        answer:
          'LeaderDaddy, a CRM by Toolcross LLP, publishes this comparison. It was reviewed on 7 October 2026 using vendor sources and repository evidence. Vendor claims are not independently tested results.',
      },
      {
        question: 'What should I verify before switching?',
        answer:
          'Test a sample import, role permissions, follow-up dates and essential integrations. Confirm the current price and any messaging or usage charges. Do not assume all data or integrations migrate automatically.',
      },
    ],
  },
};
